import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { ColumnConfig, TextAlignment } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { SortDirections, WafflesPaginationProps } from "@app/shared/waffles/exports";
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import PageSection from "@app/shared/PageSection";
import MetricsBox from "@app/shared/metrics/MetricsBox";
import MetricsList from "@app/shared/metrics/MetricsList";
import MetricsStackedBar from "@app/shared/metrics/MetricsStackedBar";
import MetricsPie from "@app/shared/metrics/MetricsPie";
import { NetworkSubmissionFilters, NetworkSubmissionMetricsModel, NetworkSubmissionTableData, ValidExceptionStatuses } from "@app/compliance-connect/network-submission/interfaces";
import { SpecialtyGroupExceptionStatus, SpecialtyGroupExceptionStatusLabels } from "@app/exceptions/enums";
import { NetworkSubmissionStatusBadge } from "@app/compliance-connect/network-submission/NetworkSubmissionStatusBadge";
import NetworkSubmissionService from "@app/compliance-connect/network-submission/networkSubmissionService";
import { NetworkSubmissionFilterBar } from "@app/compliance-connect/common/NetworkSubmissionFilterBar";
import { useNetworkSubmissionFilterOptions } from "@app/compliance-connect/network-submission/useNetworkSubmissionFilterOptions";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { getGenericErrorMessage } from "@app/shared/utility/getGenericErrorMessage";
import { ExceptionsNavFilter } from "@app/exceptions/contexts/ExceptionsFiltersContext";
import { QESButton } from "@app/shared/qes-standards/Button";
import { formatDateCustom } from "@app/shared/formatDateTime";

const backendSortColumnMap: Partial<Record<keyof NetworkSubmissionTableData, string>> = {
  counties: "counties",
  dateSubmitted: "dateSubmitted",
  exceptionsLink: "exceptions",
  fillableGaps: "fillableGaps",
  gaps: "gaps",
  membershipCount: "membershipCount",
  projectNameLink: "projectName",
  regulatorComments: "regulatorComments",
  regulatorName: "regulatorName",
  regulatorReviewStatusBadge: "regulatorReviewStatus",
  specialties: "specialties",
};

interface NetworkSubmissionOverviewProps {
  isArchivedTable: boolean;
}

const emptyFilters: NetworkSubmissionFilters = {
  regulatorClientIds: [],
  regulatoryReviewStatus: null,
  serviceAreas: [],
  specialties: [],
};

const processingStatusItems = (metrics: NetworkSubmissionMetricsModel | null) => [
  {
    color: "#008559",
    label: "Approved",
    testId: "network-submission-overview__processing-approved",
    value: metrics?.approved ?? 0,
  },
  {
    color: "#cc3a52",
    label: "Denied",
    testId: "network-submission-overview__processing-denied",
    value: metrics?.denied ?? 0,
  },
  {
    color: "#ffd654",
    label: "Pending",
    testId: "network-submission-overview__processing-pending",
    value: metrics?.pending ?? 0,
  },
];

export const NetworkSubmissionOverview: FC<NetworkSubmissionOverviewProps> = ({
  isArchivedTable,
}) => {
  const history = useHistory();
  const service = useRef(new NetworkSubmissionService()).current;

  const [pageIndex, setPageIndex] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [sortColumn, setSortColumn] = useState<string | undefined>(undefined);
  const [sortDescending, setSortDescending] = useState<boolean>(true);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

  useEffect(() => {
    setSelectedKeys([]);
  }, [pageIndex]);

  const navigateToExceptions = useCallback((customProjectId: number, projectName: string) => {
    const navFilter: ExceptionsNavFilter = {
      project: { key: customProjectId.toString(), name: projectName },
      statuses: ValidExceptionStatuses
        .map(s => ({ key: s.toString(), name: s.charAt(0).toUpperCase() + s.slice(1) })),
    };
    history.push("/exceptions/view", { exceptionsNavFilter: navFilter });
  }, [history]);

  const handlePassThroughSort = useCallback((
    rows: NetworkSubmissionTableData[],
    column: keyof NetworkSubmissionTableData,
    direction: SortDirections,
  ): NetworkSubmissionTableData[] => {
    if (direction === SortDirections.None) {
      setSortColumn(undefined);
      setSortDescending(true);
      setPageIndex(1);
      return rows;
    }

    setSortColumn(backendSortColumnMap[column]);
    setSortDescending(direction === SortDirections.Desc);
    setPageIndex(1);

    return rows;
  }, []);

  const { filterOptions, filterOptionsError: isErrorFilters, isLoading: isFetchingFilters } = useNetworkSubmissionFilterOptions();

  const { data: regulatorsData, isError: isErrorRegulators } = useQuery({
    queryFn: ({ signal }) => service.getRegulators(signal),
    queryKey: ["ns-regulators"],
  });

  useEffect(() => {
    if (isErrorRegulators) {
      toast(<span>Failed to load regulators.</span>, { containerId: "z-index-9999", type: "error" });
    }
  }, [isErrorRegulators]);

  const allServiceAreaKeys = useMemo(
    () => filterOptions?.serviceAreas.flatMap(state => state.children?.map(county => county.value) ?? []) ?? [],
    [filterOptions],
  );
  const allSpecialtyKeys = useMemo(() => filterOptions?.specialties.map(o => o.value) ?? [], [filterOptions]);
  const allRegulatorClientIds = useMemo(() => (regulatorsData ?? []).map(r => r.clientId), [regulatorsData]);

  const allSelectedFilters: NetworkSubmissionFilters = useMemo(() => ({
    ...emptyFilters,
    regulatorClientIds: allRegulatorClientIds,
    serviceAreas: allServiceAreaKeys,
    specialties: allSpecialtyKeys,
  }), [allRegulatorClientIds, allServiceAreaKeys, allSpecialtyKeys]);

  const [pendingFilters, setPendingFilters] = useState<NetworkSubmissionFilters>(emptyFilters);
  const [activeFilters, setActiveFilters] = useState<NetworkSubmissionFilters>(emptyFilters);

  const hasInitializedFilters = useRef(false);

  useEffect(() => {
    if (!hasInitializedFilters.current && filterOptions && regulatorsData) {
      hasInitializedFilters.current = true;
      setPendingFilters(allSelectedFilters);
      setActiveFilters(allSelectedFilters);
    }
  }, [filterOptions, regulatorsData, allSelectedFilters]);

  const serviceAreaIds = activeFilters.serviceAreas.length > 0 && activeFilters.serviceAreas.length < allServiceAreaKeys.length
    ? activeFilters.serviceAreas
    : undefined;
  const specialtyCodes = activeFilters.specialties.length > 0 && activeFilters.specialties.length < allSpecialtyKeys.length
    ? activeFilters.specialties
    : undefined;
  const regulatorClientIds = activeFilters.regulatorClientIds.length > 0 && activeFilters.regulatorClientIds.length < allRegulatorClientIds.length
    ? activeFilters.regulatorClientIds
    : undefined;

  const { data, isFetching } = useQuery({
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) => service.getSubmissions({
      isArchived: isArchivedTable,
      pageIndex: pageIndex - 1,
      pageSize,
      regulatorClientIds,
      reviewStatus: activeFilters.regulatoryReviewStatus ?? undefined,
      serviceAreaIds,
      sortColumn,
      sortDescending: sortDescending || undefined,
    }, signal),
    queryKey: ["ns-submissions", isArchivedTable, pageIndex, pageSize, JSON.stringify(activeFilters), sortColumn, sortDescending],
  });

  const { data: metricsData, isFetching: isFetchingMetrics, isError: isErrorMetrics } = useQuery({
    enabled: !isArchivedTable,
    queryFn: ({ signal }) => service.getSubmissionMetrics({
      regulatorClientIds,
      reviewStatus: activeFilters.regulatoryReviewStatus ?? undefined,
      serviceAreaIds,
      specialtyCodes,
    }, signal),
    queryKey: ["ns-metrics", JSON.stringify(activeFilters)],
  });

  const networkSubmissionData = data?.networkSubmissions ?? [];

  const metrics: NetworkSubmissionMetricsModel | null = metricsData != null ? {
      approved: metricsData.approved,
      denied: metricsData.denied,
      fillableGapsSegments: [
        { color: "#008559", label: "Fillable by a Single Provider", value: metricsData.fillableBySingleProviderCount },
        { color: "#ffd654", label: "Fillable by Multiple Providers", value: metricsData.fillableByMultipleProvidersCount },
        { color: "#cc3a52", label: "Unfillable", value: metricsData.unfillableGapsCount },
      ],
      pending: metricsData.pending,
      regulatorReviewStatusSegments: [
        { label: SpecialtyGroupExceptionStatusLabels[SpecialtyGroupExceptionStatus.Pending], value: metricsData.regulatorReviewStatusPending },
        { label: SpecialtyGroupExceptionStatusLabels[SpecialtyGroupExceptionStatus.Submitted], value: metricsData.regulatorReviewStatusSubmitted },
        { label: SpecialtyGroupExceptionStatusLabels[SpecialtyGroupExceptionStatus.Denied], value: metricsData.regulatorReviewStatusDenied },
        { label: SpecialtyGroupExceptionStatusLabels[SpecialtyGroupExceptionStatus.Appealed], value: metricsData.regulatorReviewStatusAppealed },
        { label: SpecialtyGroupExceptionStatusLabels[SpecialtyGroupExceptionStatus.Approved], value: metricsData.regulatorReviewStatusApproved },
        { label: SpecialtyGroupExceptionStatusLabels[SpecialtyGroupExceptionStatus.Expired], value: metricsData.regulatorReviewStatusExpired },
      ],
      total: metricsData.total,
    } : null;

  const handleSearch = () => {
    setActiveFilters(pendingFilters);
    setPageIndex(1);
  };

  const handleClearAll = () => {
    setPendingFilters(allSelectedFilters);
    setActiveFilters(allSelectedFilters);
    setPageIndex(1);
  };

  const tableData: NetworkSubmissionTableData[] = useMemo(() => {
    return networkSubmissionData.map(row => ({
      ...row,
      counties: row.counties,
      dateSubmitted: formatDateCustom(row.dateSubmitted, "MMMM D, YYYY"),
      exceptions: row.exceptions,
      exceptionsLink: (
        <QESButton qesType="link"
          onClick={() => navigateToExceptions(row.customProjectId, row.projectName)}
          qesTestId={`network-submission-exceptions-${row.customProjectId}`}>
            {row.exceptions} Exceptions
        </QESButton>
      ),
      projectName: row.projectName,
      projectNameLink: (
        <Link to={`/network-submission/project/${row.key}`}>
          {row.projectName}
        </Link>
      ),
      regulatorComments: row.regulatorComments,
      regulatorReviewStatus: row.regulatorReviewStatus,
      regulatorReviewStatusBadge: (
        <NetworkSubmissionStatusBadge status={row.regulatorReviewStatus}>
          {SpecialtyGroupExceptionStatusLabels[row.regulatorReviewStatus]}
        </NetworkSubmissionStatusBadge>
      ),
      specialties: row.specialties,
    }));
  }, [networkSubmissionData]);

  const totalRecords = data?.totalCount ?? 0;

  const paginationProps: WafflesPaginationProps = useMemo<WafflesPaginationProps>(() => {
    return {
      changePage: (pageNumber: number) => setPageIndex(pageNumber),
      enableBackendPagination: true,
      handlePageSizeChange: (size: number) => {
        setPageSize(size);
        setPageIndex(1);
      },
      pageSizeOptions: [10, 100, 1000],
      recordsPerPage: pageSize,
      resetToFirstPage: false,
      showPageSizeSelector: true,
      totalRecords: totalRecords,
    };
  }, [pageSize, totalRecords]);

  const startRecord = (pageIndex - 1) * pageSize + 1;

  const endRecord = Math.min(pageIndex * pageSize, totalRecords);

  const columnConfig: ColumnConfig = useMemo<ColumnConfig>(() => {
    return {
      counties: {
        alignment: TextAlignment.Right,
        displayText: "Counties",
        sizeContentToFit: true,
        sortOrder: 90,
        testId: "network-submission-counties",
      },
      dateSubmitted: {
        displayText: "Date Submitted",
        sizeContentToFit: true,
        sortOrder: 12,
        testId: "network-submission-date-submitted",
      },
      exceptions: {
        hidden: true,
        nosort: true,
        testId: "network-submission-exceptions-key",
      },
      exceptionsLink: {
        alignment: TextAlignment.Right,
        displayText: "Exceptions",
        sizeContentToFit: true,
        sortOrder: 70,
        testId: "network-submission-exceptions",
      },
      fillableGaps: {
        alignment: TextAlignment.Right,
        displayText: "Fillable Gaps",
        sizeContentToFit: true,
        sortOrder: 60,
        testId: "network-submission-fillable-gaps",
      },
      gaps: {
        alignment: TextAlignment.Right,
        displayText: "Total Gaps",
        sizeContentToFit: true,
        sortOrder: 50,
        testId: "network-submission-gaps",
      },
      membershipCount: {
        alignment: TextAlignment.Right,
        displayText: "Membership Count",
        sizeContentToFit: true,
        sortOrder: 80,
        testId: "network-submission-membership-count",
      },
      projectName: {
        hidden: true,
        nosort: true,
        testId: "network-submission-project-name-key",
      },
      projectNameLink: {
        displayText: "Network Submission",
        minWidth: "150px",
        sizeContentToFit: true,
        sortOrder: 10,
        testId: "network-submission-project-name",
      },
      regulatorComments: {
        alignment: TextAlignment.Right,
        displayText: "Regulator Comments",
        sizeContentToFit: true,
        sortOrder: 40,
        testId: "network-submission-regulator-comments",
      },
      regulatorName: {
        displayText: "Regulator Name",
        sizeContentToFit: true,
        sortOrder: 15,
        testId: "network-submission-regulator-name",
      },
      regulatorReviewStatus: {
        hidden: true,
        nosort: true,
        testId: "network-submission-regulator-review-status-key",
      },
      regulatorReviewStatusBadge: {
        alignment: TextAlignment.Center,
        displayText: "Regulator Review Status",
        sizeContentToFit: true,
        sortOrder: 30,
        testId: "network-submission-regulator-review-status",
      },
      specialties: {
        alignment: TextAlignment.Right,
        displayText: "Specialties",
        sizeContentToFit: true,
        sortOrder: 100,
        testId: "network-submission-specialties",
      },
    };
  }, []);

  return (
    <PageSection className="network-submission-overview">
      {isErrorFilters ? (
        <QESAlert type="danger" persist>{getGenericErrorMessage()}</QESAlert>
      ) : !isFetchingFilters && (
          <NetworkSubmissionFilterBar
          filterOptions={filterOptions}
          regulatorOptions={regulatorsData ?? null}
          onClearAll={handleClearAll}
          onPendingFiltersChange={setPendingFilters}
          onSearch={handleSearch}
          pendingFilters={pendingFilters}
        />
      )}
      {!isArchivedTable && !isFetchingMetrics && (
        isErrorMetrics ? (
          <QESAlert type="danger" persist>{getGenericErrorMessage()}</QESAlert>
        ) : (
          <AccordionContainer>
            <Accordion
              testId="network-submission-overview__key-metrics-accordion"
              title="Key Metrics"
              defaultOpen={true}
            >
              <div className="network-submission-overview__metrics-container">
                <MetricsBox label="Total" value={metrics?.total ?? 0} sublabel="Networks" />
                <MetricsList
                  label="Processing Status"
                  items={processingStatusItems(metrics)}
                />
                <MetricsStackedBar
                  label="Regulator Review Status"
                  segments={metrics?.regulatorReviewStatusSegments ?? []}
                />
                <MetricsPie
                  label="Total Gaps"
                  segments={metrics?.fillableGapsSegments ?? []}
                />
              </div>
            </Accordion>
          </AccordionContainer>
        )
      )}
      <p>Displaying {totalRecords > 0 ? `${startRecord}-${endRecord} of ${totalRecords}` : "0"} records</p>
      <Waffles<NetworkSubmissionTableData>
        columns={columnConfig}
        customSort={handlePassThroughSort}
        data={tableData}
        enablePagination={true}
        fullWidth={true}
        handleCheckboxChange={!isArchivedTable ? setSelectedKeys : undefined}
        loading={isFetching}
        paginationProps={paginationProps}
        passThroughSorting={true}
        selectedKeys={!isArchivedTable ? selectedKeys : undefined}
        showCheckboxes={!isArchivedTable}
        testId="network-submission-waffles-table"
      />
    </PageSection>
  );
};
